import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { logError } from 'utils/datadog';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Error boundary for the public guest CV route. That route sits OUTSIDE the authenticated
 * subtree, which is the only place with a react-router `errorElement` — so without this an
 * uncaught render error (e.g. a stale dynamic-import chunk after a deploy, or a component
 * throw on a constrained mobile browser) white-screens the applicant AND is never reported
 * to Datadog RUM. This catches the throw, reports it with guest-flow context, and shows a
 * recoverable fallback.
 *
 * Copy is intentionally hardcoded (not i18n): the boundary must render even if a provider in
 * the crashed subtree is the thing that failed.
 */
export class GuestCvErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logError('Guest CV render crash', error, {
      feature: 'guest-cv',
      componentStack: info.componentStack ?? undefined
    });
  }

  // Reloading re-redeems the still-valid magic-link token in the URL (?t=) and resumes the
  // applicant at their saved step, rather than dead-ending on a blank screen.
  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 2,
          px: 3
        }}
      >
        <Typography variant="h4">Etwas ist schiefgelaufen</Typography>
        <Typography variant="body2" color="text.secondary">
          Bitte lade die Seite neu, um fortzufahren. Deine bisherigen Angaben
          sind gespeichert.
        </Typography>
        <Button variant="contained" onClick={this.handleReload}>
          Seite neu laden
        </Button>
      </Box>
    );
  }
}
